import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as markdown from '@attain-apps/fe/web/main/lib/markdown'

import * as info from './info'

const MAX_MESSAGE_HEIGHT_PX = 200

/**
 * Displays a chat message with support for markdown content, file attachments, and status indicators
 */
@ngCore.Component({
  imports: [ds.overlay.attached.Component, markdown.Component, info.Component],
  selector: 'atn-assistant-message',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** Reference to the content wrapper element */
  private readonly _contentWrapperRefSg =
    ngCore.viewChild<ngCore.ElementRef<HTMLDivElement>>('contentWrapper')

  /**
   * The {@link ents.core.agent.Message} to display
   */
  public readonly messageSg = ngCore.input.required<ents.core.agent.Message.WithInfo>({
    alias: 'message',
  })

  /**
   * Effective info stream used for rendering.
   */
  private readonly _effectiveInfoSg = ngCore.computed<
    readonly ents.core.agent.Info[]
  >(() => this.messageSg().info)

  /** Assistant answer text, excluding files, citations, and reasoning events. */
  private readonly _answerTextSg = ngCore.computed(
    (_info = this._effectiveInfoSg()) =>
      _info
        .filter(ents.core.agent.Info.Text.isText.raw)
        .map(entry => entry.text)
        .join(''),
  )

  /** Citation entries used to annotate the answer text. */
  private readonly _citationInfoSg = ngCore.computed(
    (_info = this._effectiveInfoSg()) =>
      _info.filter(ents.core.agent.Info.Citation.isCitation.raw),
  )

  /**
   * The `file` Info entries projected as file names; drives the attachments UI.
   */
  public readonly attachedFilesSg = ngCore.computed<readonly string[]>(
    (_info = this._effectiveInfoSg()) =>
      _info
        .filter(ents.core.agent.Info.File.isFile.raw)
        .map(entry => entry.name),
  )

  /**
   * Every Info entry except plain text and file references; i.e. the
   * reasoning timeline (rationale, status, code, tool use, errors).
   */
  public readonly reasoningInfoSg = ngCore.computed<
    readonly ents.core.agent.Info[]
  >((_info = this._effectiveInfoSg()) =>
    _info.filter(ents.core.agent.Info.Reasoning.isReasoning.raw),
  )

  /**
   * Markdown-ready answer text with inline citation markers, plus the deduped
   * source list used by the Sources drawer.
   */
  public readonly citationDisplaySg =
    ngCore.computed<ents.core.agent.Info.Citation.Display>(
      (citations = this._citationInfoSg(), text = this._answerTextSg()) =>
        ents.core.agent.Info.Citation.Display.from({
          citations,
          text,
        }),
    )

  /** Signal tracking whether the message is expanded */
  public readonly expandedSg = ngCore.signal(false)

  /** Signal controlling visibility of the "Show more / Show less" toggle button */
  public readonly showMoreToggleSg = ngCore.signal(false)

  /** Maximum expanded content height used for the show more / show less transition */
  public readonly contentMaxHeightPxSg = ngCore.signal(MAX_MESSAGE_HEIGHT_PX)

  /** Signal controlling visibility of the attachments overlay */
  public readonly attachmentsOverlayOpenSg = ngCore.signal(false)
  /** Signal controlling visibility of the citation sources overlay */
  public readonly sourcesOverlayOpenSg = ngCore.signal(false)
  /** Event emitted when a file download is requested */
  public readonly fileDownload = ngCore.output<{
    message: ents.core.agent.Message
    file: ents.core.agent.Message.File
  }>()
  /** Event emitted when a retry is requested */
  public readonly retry = ngCore.output<ents.core.agent.Message>()

  constructor() {
    // `onMarkdownReady()` may not fire again once streaming ends, if the
    // final chunk's text is identical to the last one rendered while
    // pending (no `[data]` change to re-render). Re-check here so a message
    // that finished long still gets its "Show more" toggle.
    ngCore.effect(() => {
      if (this.messageSg().status === 'pending') {
        return
      }
      requestAnimationFrame(() => {
        const scrollHeight = this._syncContentMaxHeight()
        this.showMoreToggleSg.set(scrollHeight > MAX_MESSAGE_HEIGHT_PX)
      })
    })
  }

  /**
   * Called when the markdown content has finished rendering.
   * Updates whether the "Show more / Show less" toggle should be visible
   * based on the scroll height of the message content.
   *
   * While the message is still streaming (`status === 'pending'`), content
   * is kept fully expanded instead — clamping mid-stream would collapse text
   * out from under the user as it's still arriving.
   */
  public onMarkdownReady(): void {
    // Defer to next frame to ensure layout is complete and scrollHeight is accurate
    requestAnimationFrame(() => {
      const scrollHeight = this._syncContentMaxHeight()
      if (this.messageSg().status === 'pending') {
        this.expandedSg.set(true)
        return
      }
      this.showMoreToggleSg.set(scrollHeight > MAX_MESSAGE_HEIGHT_PX)
    })
  }

  /** Toggle expand/collapse state when the button is clicked */
  public toggleExpandButtonClickHandler(): void {
    this._syncContentMaxHeight()
    this.expandedSg.update(expanded => !expanded)
  }

  /** Syncs the expanded max-height with rendered markdown content. */
  private _syncContentMaxHeight(): number {
    const scrollHeight =
      this._contentWrapperRefSg()?.nativeElement.scrollHeight ??
      MAX_MESSAGE_HEIGHT_PX

    this.contentMaxHeightPxSg.set(Math.max(scrollHeight, MAX_MESSAGE_HEIGHT_PX))

    return scrollHeight
  }
}
